<template>
    <Invoices
        v-if="loaded"
        :invoices="invoices"
        :paid-invoices="paidInvoices"
        :paid-invoices-per-page="paidInvoicesPerPage"
        :show-filter-options="showFilterOptions"
        :invoices-per-page="invoicesPerPage"
        :write-off-total="writeOffTotal"
        :collections-total="collectionsTotal"
        :outstanding-total="outstandingTotal"
        :exclude-columns="excludeColumns"
        @fetchPaidInvoices="fetchPaidInvoices"
        @invoicesPerPageUpdated="setInvoicesPerPage($event)"
        @paidInvoicesPerPageUpdated="setPaidInvoicesPerPage($event)"
        @filtersChanged="fetchInvoices($event)"
        @downloadFile="downloadFile"
    />
</template>

<script>
import Vue from "vue";
import { StripePlugin } from "@vue-stripe/vue-stripe";
import { mapState, mapActions } from "vuex";
import swal from "sweetalert2";
import Invoices from "./_components/Invoices";
import api from "src/services/api.js";
import { store, getters, mutations } from "src/global-store.js";

export default {
    name: "InvoicesModule",
    components: {
        Invoices,
    },
    beforeRouteUpdate(to, from, next) {
        if (to.query.payment_intent_client_secret) {
            this.showPaymentStatus(to.query.payment_intent_client_secret);
        }
        next();
    },
    data() {
        return {
            invoices: [],
            paidInvoices: [],
            loaded: false,
            writeOffTotal: 0,
            collectionsTotal: 0,
            outstandingTotal: 0,
        };
    },
    computed: {
        me() {
            return store.me;
        },
        hasClientRole() {
            return getters.hasClientRole();
        },
        hasCompanyRole() {
            return getters.hasCompanyRole();
        },
        ...mapState("invoice", {
            invoicesPerPage: (state) => state.invoicesPerPage,
            paidInvoicesPerPage: (state) => state.paidInvoicesPerPage,
        }),
        showFilterOptions() {
            return this.hasCompanyRole;
        },
        excludeColumns() {
            const columns = [];
            if (this.hasClientRole) {
                columns.push("Customer");
                columns.push("Status");
            }
            return columns;
        },
    },
    async created() {
        mutations.setNavbarTitle("Invoices");
        if (this.$route.query.payment_intent_client_secret) {
            this.showPaymentStatus(
                this.$route.query.payment_intent_client_secret,
            );
        }
        this.invoices = await this.fetchInvoices(["1", "2", "4"]);

        if (this.hasCompanyRole) {
            try {
                const response = await api.get("/invoices/totals");
                this.collectionsTotal = response.collections_total;
                this.outstandingTotal = response.outstanding_total;
                this.writeOffTotal = response.writeoff_total;
            } catch (error) {
                console.error(error);
            }
        }
        mutations.setNavbarSubtitle(this.me?.customer_name);
        this.loaded = true;
    },
    methods: {
        ...mapActions(["downloadFile"]),
        ...mapActions("invoice", [
            "setInvoicesPerPage",
            "setPaidInvoicesPerPage",
        ]),
        async fetchInvoices(paymentStatuses = [], type = "") {
            try {
                return api.get(
                    "/invoices?status=" +
                        paymentStatuses.join(",") +
                        "&type=" +
                        type,
                );
            } catch (error) {
                console.error(error);
            }
        },
        async fetchPaidInvoices() {
            this.paidInvoices = await this.fetchInvoices([], "paid");
        },
        showPaymentStatus(paymentIntentClientSecret) {
            Vue.use(StripePlugin, {
                pk: process.env.VUE_APP_STRIPE_PK_KEY,
            });

            this.$stripe
                .retrievePaymentIntent(paymentIntentClientSecret)
                .then(({ paymentIntent }) => {
                    let message = {
                        buttonsStyling: false,
                    };
                    switch (paymentIntent.status) {
                        case "succeeded":
                            message.title = "Success! Payment received.";
                            message.text = `Thank you for your payment of ${paymentIntent.description}.`;
                            message.confirmButtonClass =
                                "btn btn-success btn-fill";
                            message.type = "success";
                            break;

                        case "processing":
                            message.title = "Payment processing...";
                            message.text = `We are processing your payment of ${paymentIntent.description}. We will email you when your payment succeeds.`;
                            message.confirmButtonClass = "btn btn-fill";
                            message.type = "info";
                            break;

                        case "requires_action":
                            message.title = "Requires action";
                            message.text = `Your payment of ${paymentIntent.description} requires further action. You should receive an email on the next steps that are required to complete your payment.`;
                            message.confirmButtonClass = "btn btn-fill";
                            message.type = "info";
                            break;

                        default:
                            message.title = "Payment failed";
                            message.text = `We are sorry, there was an error processing your payment of ${paymentIntent.description}. Please try again with a different payment method.`;
                            message.confirmButtonClass = "btn btn-fill";
                            message.type = "error";
                            break;
                    }
                    swal(message).then(() => {
                        this.$router.replace({ query: null });
                    });
                });
        },
    },
};
</script>
